import { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { APIFieldType, FieldType } from "../../../services/helpers/types";
import { useDispatch } from "react-redux";

import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import DraggableFieldsItem from "../DraggableFieldsItem/DraggableFieldsItem";
import { setFieldsPositions } from "../../../services/edit/fieldsEdit.service";
import { deleteField } from "../../../services/api/fields.api";

interface DraggableFieldsListProps {
  fields: FieldType[];
  onPositionChange: (newItems: FieldType[]) => void;
  onEditField: (itemID: number, newField: APIFieldType) => void;
  onCreateField: (fieldID: number, newField: APIFieldType) => void;
  onDelete: (itemID: number) => void;
}

const DraggableFieldsList = (props: DraggableFieldsListProps) => {
  const { fields, onPositionChange, onEditField, onCreateField, onDelete } =
    props;

  const dispatch = useDispatch();

  const [items, setItems] = useState<number[]>([]);

  useEffect(() => {
    const fieldIds = fields
      .filter((field) => !field.fieldID.toString().includes("more-info")) // Removes more info field
      .map((field) => parseInt(field.fieldID.toString()));
    setItems(fieldIds);
  }, [fields]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor)
  );

  const handleChangePosition = (newItems: number[]) => {
    const newFields: FieldType[] = newItems.map((item, index) => {
      const newItem = fields.find((field) => field.fieldID === item);
      if (!newItem) {
        return fields[index];
      }
      return { ...newItem, position: index + 1 };
    });
    setFieldsPositions(dispatch, newFields);
    onPositionChange(newFields);
  };

  const updatedItems = (items: number[], active: any, over: any) => {
    const oldIndex = items.indexOf(active.id);
    const newIndex = items.indexOf(over.id);

    return arrayMove(items, oldIndex, newIndex);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const newItems = updatedItems(items, active, over);
      setItems(newItems);
      handleChangePosition(newItems);
    }
  };

  const handleDelete = (itemID: number) => {
    setItems(items.filter((id) => id !== itemID));
    deleteField(dispatch, itemID).then((response) => {
      onDelete(itemID);
    });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {items.map((id, index) => (
          <DraggableFieldsItem
            key={id}
            id={id}
            field={fields.find((item) => item.fieldID === id)}
            onDelete={handleDelete}
            onCreateField={onCreateField}
            onEditField={onEditField}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default DraggableFieldsList;
